
import { ORDER_SHORTCUT } from "@/consts";
import Vue from "vue";
import OrderActions from "@/components/order/OrderActions.vue";
import OrderHeader from "@/components/order/OrderHeader.vue";
import OrderConsigneeContacts from "@/components/order/OrderConsigneeContacts.vue";
import OrderCommodities from "@/components/order/OrderCommodities.vue";
import OrderNotes from "@/components/order/OrderNotes.vue";
import OrderReferenceNumbers from "@/components/order/OrderReferenceNumbers.vue";
import OrderBarcodes from "@/components/order/OrderBarcodes.vue";
import OrderShipsWithOrders from "@/components/order/OrderShipsWithOrders.vue";
import OrderDocuments from "@/components/order/OrderDocuments.vue";
import OrderBackordered from "@/components/order/OrderBackordered.vue";
import notifications from "@/lib/notifications";
import { Component } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import OrderModule from "@/store/modules/order";
import SystemModule from "@/store/modules/system";
import { DefaultTextArea } from "@/models/api/orders/defaults";
import OrderTextCards from "@/components/order/OrderTextCards.vue";
import permissions from "@/security/permissions";
import { IOrderResource } from "@/store/types";
import OrderParty from "@/components/order/OrderParty.vue";
import AddRequiredStockModal from "@/components/widgets/Modals/AddRequiredStockModal.vue";
import BarcodeOrderModule from "@/store/modules/barcodeSystem/barcodeOrder";
import BarcodeCompanyModule from "@/store/modules/barcodeSystem/barcodeCompany";
import BarcodeManufacturerModule from "@/store/modules/barcodeSystem/barcodeManufacturer";
import { BarcodeOrderResource } from "@/models/api/barcodeSystem/barcodeOrders/get";

@Component({
  components: {
    OrderActions,
    OrderHeader,
    OrderConsigneeContacts,
    OrderCommodities,
    OrderNotes,
    OrderReferenceNumbers,
    OrderBarcodes,
    OrderShipsWithOrders,
    OrderDocuments,
    OrderBackordered,
    OrderTextCards,
    AddRequiredStockModal
  }
})
export default class OrderDetail extends Vue {
  orderModule: OrderModule = getModule(OrderModule);
  systemModule: SystemModule = getModule(SystemModule);
  barcodeOrderModule: BarcodeOrderModule = getModule(BarcodeOrderModule);
  barcodeCompanyModule: BarcodeCompanyModule = getModule(BarcodeCompanyModule);
  barcodeManufacturerModule: BarcodeManufacturerModule = getModule(BarcodeManufacturerModule);
  orderAction: string = "";
  showTrucks: boolean = false;
  shortcuts = ORDER_SHORTCUT;
  tabIsLoading: boolean = false;
  loadedTabs: Array<string> = [];
  hasBackorders: any[] = [];
  hasShortages: any[] = [];
  hasDamages: any[] = [];
  showAddRequiredStockModal: boolean = false;
  requiredStockBarcodeOrder: BarcodeOrderResource = null;

  get shortcutsToDisplay(): Array<any> {
    return this.shortcuts.filter(x => !this.loading &&
                                      (x.action != "order/viewBarcodeOrderShortcutAction" || (this.currentOrder.barcodeOrderId)) &&
                                      (x.action != "barcodeOrder/linkInventoryToOrderShortcutAction" || (this.currentOrder.inventoryLinkable)));
  }

  get canAddRequiredStock(): boolean {
    if (!this.hasPermission(permissions.barcodeSystem.requiredStock.save)) return false;
    if (!this.requiredStockBarcodeOrder) return false;
    if (["NS", "R"].indexOf(this.requiredStockBarcodeOrder.status) < 0) return false;
    return this.isBillToAllowedForManualStockOrders(this.requiredStockBarcodeOrder.billToCompanyId);
  }

  isBillToAllowedForManualStockOrders(billToCompanyId: string): boolean {
    if (!billToCompanyId) return false;
    const billTo = (this.barcodeCompanyModule.billTos || []).find(x => x.billToCompanyId === billToCompanyId);
    if (!billTo) return false;
    return (this.barcodeManufacturerModule.manufacturers || []).some(x => x.allowManualStockOrdersCreation && x.companyIds && x.companyIds.includes(billTo.companyId));
  }

  get exceptionBanner(): string | null {
    const parts: string[] = [];
    if (this.currentOrder?.isOnHold) parts.push("Customer is on credit hold");
    if (this.hasBackorders.length > 0) parts.push("Backordered Items");
    if (this.hasDamages.length > 0) parts.push("Damage");
    if (this.hasShortages.length > 0) parts.push("Shortage");
    return parts.length > 0 ? parts.join(" | ") : null;
  }

  get currentOrder(): IOrderResource {
    return this.orderModule.currentOrder;
  }

  get loading(): boolean {
    return this.orderModule.loading;
  }

  get intermediateConsignee(): string {
    return this.orderModule.currentOrder ? this.orderModule.currentOrder.intermediateConsignee : "";
  }

  get displayShipWithOrderCheckbox() : boolean {
    return this.orderModule.currentOrder ? this.orderModule.currentOrderShipsWithOrders.length != 0 : false;
  }

  set intermediateConsignee(val: string) {
    this.orderModule.updateIntermediateConsignee(val);
  }

  get remarks(): string {
    return this.orderModule.currentOrder.remarks ? this.orderModule.currentOrder.remarks : "";
  }
  set remarks(val: string) {
    this.orderModule.updateRemarks(val);
  }

  get specialRequests(): string {
    return this.orderModule.currentOrder.specialRequests
      ? this.orderModule.currentOrder.specialRequests
      : "";
  }
  set specialRequests(val: string) {
    this.orderModule.updateSpecialRequests(val);
  }

  get driverNote(): string {
    return this.orderModule.currentOrder.driverNote ? this.orderModule.currentOrder.driverNote : "";
  }

  set driverNote(val: string) {
    this.orderModule.updateDriverNote(val);
  }

  async created() {
    // Reload Current Order from the index
    try {
      await this.orderModule.loadOrder(this.$route.params.idx);
      this.orderModule.getOrderShipsWithOrders(this.orderModule.currentOrder)
      await this.systemModule.loadReferenceTypes(this.currentOrder.deliveryForId??0);
      const orderNumber = Number(this.currentOrder.number);
      [this.hasBackorders, this.hasShortages, this.hasDamages] = await Promise.all([
        this.orderModule.hasBackorders(orderNumber),
        this.orderModule.hasShortages(orderNumber),
        this.orderModule.hasDamages(orderNumber)
      ]);
      if (this.currentOrder.barcodeOrderId) {
        this.requiredStockBarcodeOrder = await this.barcodeOrderModule.getBarcodeOrder(this.currentOrder.barcodeOrderId);
      }
    } catch (ex) {
      notifications.error("Failed to load order", "Failed to load order.", ex);
    }
  }

  partyUpdated(party: OrderParty, type: string) {
    this.orderModule.changeCurrentOrderParty({ party: party, type: type });
  }

  tabHasBeenLoaded(title: string): boolean {
    return this.loadedTabs.indexOf(title) > -1;
  }

  loadTab(title: string) {
    if (this.tabHasBeenLoaded(title)) {
      return;
    }

    let action = null;

    switch (title) {
      case "notes":
        action = this.orderModule.getOrderNotes;
        break;
      case "reference":
        action = this.orderModule.getOrderReferenceNumbers;
        break;
      case "barcodes":
        action = this.orderModule.getOrderBarcodes;
        break;
      case "documents":
        action = this.orderModule.loadOrderThumbnails;
        break;
    }
    if (action) {
      this.tabIsLoading = true;
      action(this.orderModule.currentOrder)
      .catch((error) => {
        notifications.error("Error loading tab", `Failed to load ${title} tab.`, error);
      })
      .finally(() => {
        this.loadedTabs.push(title);
        this.tabIsLoading = false;
      });
    }
  }
}
